import { router, usePage } from "@inertiajs/react";
import { Eye, EyeClosed, Loader2, Lock } from "lucide-react";
import type { ComponentProps } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
	AlertDialog,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getCsrfToken } from "@/lib/csrf";
import type { UserForAdmin } from "@/types/admin-portal/user";
import type { GlobalPageProps } from "@/types/globals";

export interface DeleteUserContentProps extends ComponentProps<"div"> {
	user: UserForAdmin | null;
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

export function DeleteUserDialogContent({
	user,
	open,
	onOpenChange,
}: DeleteUserContentProps) {
	const { t } = useTranslation("users");
	const { props: globalProps } = usePage<GlobalPageProps>();
	const [isLoading, setIsLoading] = useState(false);
	const [confirmPassword, setConfirmPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);

	const adminEmail = globalProps.auth?.currentUser?.user?.email;

	async function handleDelete() {
		if (!user) return;
		if (!confirmPassword) {
			toast.error(t("messages.password_required", "Password is required"));
			return;
		}

		setIsLoading(true);
		try {
			const csrfToken = getCsrfToken();

			const response = await fetch(`/admin-portal/user-management/${user.id}`, {
				method: "DELETE",
				headers: {
					"Content-Type": "application/json",
					Accept: "application/json",
					"X-CSRF-Token": csrfToken,
				},
				body: JSON.stringify({
					admin_email: adminEmail,
					confirm_password: confirmPassword,
				}),
			});

			const data = await response.json();

			if (response.ok && data.success) {
				toast.success(data.message || t("messages.delete_success"));
				onOpenChange(false);
				router.visit("/admin-portal/user-management");
			} else {
				toast.error(data.message || t("messages.delete_error"));
			}
		} catch (_error) {
			toast.error(t("messages.delete_error"));
		} finally {
			setIsLoading(false);
		}
	}

	return (
		<AlertDialog
			open={open}
			onOpenChange={(val) => {
				if (!isLoading) {
					onOpenChange(val);
					if (!val) setConfirmPassword("");
				}
			}}
		>
			<AlertDialogContent>
				<AlertDialogHeader>
					<AlertDialogTitle>{t("confirm.delete_title")}</AlertDialogTitle>
					<AlertDialogDescription>{t("confirm.delete")}</AlertDialogDescription>
					<div className="mt-4 space-y-2">
						<p className="text-sm font-medium text-foreground">
							{t(
								"confirm.enter_password",
								"Please enter your password to confirm",
							)}
						</p>
						<Input
							type={showPassword ? "text" : "password"}
							placeholder={t("confirm.password_placeholder", "Admin Password")}
							value={confirmPassword}
							onChange={(e) => setConfirmPassword(e.target.value)}
							StartIcon={{ icon: Lock }}
							EndIcon={{
								icon: showPassword ? EyeClosed : Eye,
								isButton: true,
								handleOnClick: () => setShowPassword((prev) => !prev),
							}}
							disabled={isLoading}
						/>
					</div>
				</AlertDialogHeader>
				<AlertDialogFooter>
					<AlertDialogCancel disabled={isLoading}>
						{t("button.cancel")}
					</AlertDialogCancel>
					<Button
						variant="destructive"
						disabled={isLoading || !confirmPassword}
						onClick={handleDelete}
					>
						{isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
						{t("button.delete")}
					</Button>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);
}
